import gsap from 'gsap';
import Swiper from 'swiper';
import { Autoplay, EffectFade, Scrollbar } from 'swiper/modules';
import { createApp, onMounted, ref } from 'vue';

import { useVideo } from '~/composables/use-video';
import { ANIMATION_CONFIGS, ANIMATION_DURATIONS } from '~/constants/animations';
import * as DataDogLogs from '~/lib/datadog/datadog';

import * as AnalyticsData from '../analytics/analytics-data';
import { BaseSection } from './base-section';

/**
 * @type {import('swiper/types').SwiperOptions}
 */
const SWIPER_SETTINGS = {
  autoplay: {
    delay: 5000,
    disableOnInteraction: false,
  },
  effect: 'fade',
  loop: true,
  modules: [Autoplay, EffectFade, Scrollbar],
  on: {
    autoplayTimeLeft: function (swiper, _, percentage) {
      if (swiper.scrollbar.dragEl) {
        const maxWidth = 100 / swiper.slides.length;
        swiper.scrollbar.dragEl.style.width = (1 - percentage) * maxWidth + '%';
      }
    },
  },
  scrollbar: {
    draggable: true,
    el: '.swiper-scrollbar',
    hide: false,
  },
  slidesPerView: 1,
};

const SECTION_NAME = 'full-bleed-hero';

class FullBleedHero extends BaseSection {
  constructor() {
    super(`[data-section-type="${SECTION_NAME}"]`);
  }

  init(instanceData) {
    const container = instanceData.container;
    const autoplayDelay = Number(container.getAttribute('data-autoplay-delay'));
    const sectionInstance = this;

    createApp({
      name: 'FullBleedHeroSection',
      setup() {
        const videoRefs = ref({});
        const carousel = ref(null);
        const isPlaying = ref(true);

        const togglePlayPause = () => {
          isPlaying.value = !isPlaying.value;
          if (carousel.value && carousel.value.autoplay) {
            carousel.value.autoplay[isPlaying.value ? 'resume' : 'pause']();
          }
        };

        onMounted(() => {
          // Initialize videos after DOM is ready
          const videos = container.querySelectorAll('video');

          videos.forEach((video) => {
            if (video.id) {
              // Initialize the use-video composable and store its return values
              const videoComposable = useVideo(video.id);
              videoComposable.init();
              videoRefs.value[video.id] = videoComposable;
            } else {
              DataDogLogs.warn(
                `[Allbirds section] - FullBleedHero - onMounted - Video found without ID`,
                {
                  video,
                },
              );
            }
          });

          // Initialize Swiper if there are multiple slides
          const slides = container.querySelectorAll('.swiper-slide');
          if (slides.length > 1) {
            carousel.value = new Swiper(container.querySelector('.swiper'), {
              ...SWIPER_SETTINGS,
              autoplay: {
                delay: autoplayDelay,
                disableOnInteraction: false,
              },
            });
          }

          // Hook analytics event to CTA links
          if (sectionInstance.isHomePageTemplate()) {
            AnalyticsData.hookHomepageHeroClicked({ sectionName: SECTION_NAME });
          }
        });

        return {
          isPlaying,
          togglePlayPause,
          videoRefs,
        };
      },
    }).mount(container);
  }

  /**
   * Clean up resources when the section is unloaded
   * @param {Object} instanceData
   */
  onUnload(instanceData) {
    // Kill ScrollTrigger instances
    if (instanceData.animation && instanceData.animation.scrollTrigger) {
      instanceData.animation.scrollTrigger.kill();
    }

    // Kill the animation
    if (instanceData.animation) {
      instanceData.animation.kill();
      instanceData.animation = null;
    }

    // Clean up Vue app
    const app = instanceData.container.__vue_app__;
    if (app) {
      app.unmount();
    }
  }

  /**
   * Sets up the animation timeline for the section that will play when it comes into view
   * This will be called automatically during initialization
   *
   * @param {Object} instanceData - The section instance data
   * @return {Object} The GSAP timeline for the animation
   */
  setupInViewAnimationTimeline(instanceData) {
    const container = instanceData.container;
    const heroContent = container.querySelectorAll('[data-hero-content]');
    const heroControls = container.querySelector('[data-hero-controls]');
    if (!container || !heroContent) return null;

    const tl = gsap.timeline({
      paused: true,
      scrollTrigger: {
        trigger: container,
        ...this.getInViewAnimationOptions(),
      },
    });

    if (!tl) return null;

    tl.to(container, ANIMATION_CONFIGS.FADE_IN, 0);
    tl.to([heroContent, heroControls], ANIMATION_CONFIGS.FADE_IN, ANIMATION_DURATIONS.IN_VIEW);

    return tl;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new FullBleedHero();
});
